// Shared ⋮ menu for device cards: Settings (rename/wifi/props hub), Device Info,
// plus any card-specific extra items. Replaces the per-card menu + info dialog.

import React, { useEffect, useState } from 'react';
import {
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, Typography, Box,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { AddWifiDialog, RemoveWifiDialog } from '../../shared/DeviceWifiDialogs';
import { useApp } from '../../../core/state/AppContext';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { deviceLabel } from '../../../core/devices/deviceMeta';
import { getDeviceWifi } from '../../../core/devices/deviceWifi';
import { isGlassPlate } from '../../../core/devices/glassPlates';
import { FINISH, setGlassFinish } from '../../../core/devices/glassFinish';
import TextureIcon from '@mui/icons-material/Texture';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShareQrDialog from '../../shared/ShareQrDialog';
import Snackbar from '@mui/material/Snackbar';
import { updateMarkLabel } from '../../../core/devices/firmwareUpdate';
import WifiPasswordValue from '../../shared/WifiPasswordValue';
import DeviceSettingsDialog from './DeviceSettingsDialog';
import { FIT_XS, FIT_MD } from '../../shared/fitDialog';
import DeviceTechDetails from '../../shared/DeviceTechDetails';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UnassignDeviceButton from '../../shared/UnassignDeviceButton';

function InfoRow({ label, value }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
      <span>{label}</span><b style={{ color: 'inherit', textAlign: 'right' }}>{String(value ?? '—')}</b>
    </Typography>
  );
}

export default function DeviceCardMenu({ device, extraItems = [], onChanged }) {
  const { canUpdateFirmware, isSuperAdmin, realUserId, userId } = useApp();
  // The network the device reports it is joined to; the password only for a
  // Super Admin or the device's owner (core/devices/deviceWifi). Fetched when
  // Device Info opens, dropped when it closes.
  const [wifi, setWifi] = useState({ name: null, password: null });
  const [anchor, setAnchor] = useState(null);
  const [finishMsg, setFinishMsg] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  useEffect(() => {
    if (!infoOpen) return undefined;
    let alive = true;
    getDeviceWifi(device, { isSuperAdmin, userId: realUserId }).then((w) => { if (alive) setWifi(w); });
    return () => { alive = false; setWifi({ name: null, password: null }); };
  }, [infoOpen, device, isSuperAdmin, realUserId]);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [unassignSignal, setUnassignSignal] = useState(0);
  const [wifiOpen, setWifiOpen] = useState(null);   // 'add' | 'remove' | null
  const [shareOpen, setShareOpen] = useState(false);
  const uno = device.UniqueNumber;

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setSettingsOpen(true); setAnchor(null); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setInfoOpen(true); setAnchor(null); }}>
          <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Device Info</ListItemText>
        </MenuItem>
        {canUpdateFirmware && (
          <MenuItem onClick={() => { setUpdateOpen(true); setAnchor(null); }}>
            <ListItemIcon><SystemUpdateAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Update Code</ListItemText>
          </MenuItem>
        )}
        {/* The Message Box centre pane's own two dialogs (shared/DeviceWifiDialogs):
            Add picks from the location's known networks; Remove asks the chip
            what it holds and ✕ one. Room/location-wide changes stay on the
            WIFI (LOCATION) / room buttons (WifiFleetDialog). */}
        <MenuItem onClick={() => { setWifiOpen('add'); setAnchor(null); }}>
          <ListItemIcon><WifiIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add Wi-Fi</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setWifiOpen('remove'); setAnchor(null); }}>
          <ListItemIcon><WifiOffIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Remove Wi-Fi</ListItemText>
        </MenuItem>
        {/* Wi-Fi and BLE alike; the server decides who may (core/share/shareAccess). */}
        <MenuItem onClick={() => { setShareOpen(true); setAnchor(null); }}>
          <ListItemIcon><QrCode2Icon fontSize="small" /></ListItemIcon>
          <ListItemText>Share (QR)</ListItemText>
        </MenuItem>
        {/* The plate's finish — a fact of THIS installed unit (glassFinish). */}
        {isGlassPlate(device.DeviceID) && [FINISH.MATT, FINISH.GLOSSY].map((f) => (
          <MenuItem
            key={f}
            onClick={async () => {
              setAnchor(null);
              const r = await setGlassFinish(device.UniqueNumber, f);
              setFinishMsg(r.ok ? '' : r.message);
            }}
          >
            <ListItemIcon><TextureIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{f === FINISH.MATT ? 'Finish: Matt' : 'Finish: Glossy'}</ListItemText>
          </MenuItem>
        ))}
        {/* Super Admin only (the dialog checks again, and asks for the password):
            off the owner AND every user, and back to a fresh piece. */}
        {isSuperAdmin && (
          <MenuItem onClick={() => { setUnassignSignal((n) => n + 1); setAnchor(null); }}>
            <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Unassign (all users)" />
          </MenuItem>
        )}
        {extraItems.map((it) => (
          <MenuItem key={it.label} onClick={() => { it.onClick(); setAnchor(null); }}>
            {it.icon && <ListItemIcon>{it.icon}</ListItemIcon>}
            <ListItemText>{it.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <UnassignDeviceButton
        uniqueNumber={uno}
        hideTrigger
        openSignal={unassignSignal}
        // The room re-reads its devices once the report is closed: the card goes.
        onClosedAfterDone={() => {
          if (onChanged) onChanged();
          window.dispatchEvent(new CustomEvent('sli:devices-changed', { detail: { uniqueNumber: uno } }));
        }}
      />

      <AddWifiDialog
        open={wifiOpen === 'add'} uno={uno}
        subtitle={[device?.LocationName, device?.RoomName, device?.DeviceName].filter(Boolean).join(' / ')}
        onClose={() => setWifiOpen(null)}
      />
      <RemoveWifiDialog open={wifiOpen === 'remove'} uno={uno} onClose={() => setWifiOpen(null)} />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'device', device, name: device?.DeviceName || device?.UniqueNumber }}
        onClose={() => setUpdateOpen(false)}
      />

      <ShareQrDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        target={{ scope: 'device', uniqueNumber: uno }}
        name={device.DeviceName || uno}
      />

      <DeviceSettingsDialog
        device={device} open={settingsOpen}
        onClose={() => setSettingsOpen(false)} onSaved={onChanged}
      />

      <Snackbar open={!!finishMsg} autoHideDuration={4000} onClose={() => setFinishMsg('')} message={finishMsg} />

      {/* Wide, in columns, for a Super Admin (basic | ids + firmware | the chip's
          Wi-Fi) — every fact visible at once instead of a long scroll. */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} {...(isSuperAdmin ? FIT_MD : FIT_XS)}>
        <DialogTitle>{device.DeviceName || uno}</DialogTitle>
        <DialogContent
          dividers
          sx={isSuperAdmin ? {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, auto) minmax(300px, auto) minmax(240px, auto)' },
            columnGap: 3,
            rowGap: 1.5,
            alignItems: 'start',
          } : undefined}
        >
          <Box sx={{ minWidth: 0 }}>
            <InfoRow label="Unique Number" value={uno} />
            <InfoRow label="Type" value={deviceLabel(device.DeviceID)} />
            <InfoRow label="Device ID" value={device.DeviceID} />
            {device.RoomName && <InfoRow label="Room" value={device.RoomName} />}
            {device.LocationName && <InfoRow label="Location" value={device.LocationName} />}
            <InfoRow label="Wi-Fi" value={wifi.name || '—'} />
            <InfoRow
              label="Last code updated"
              value={wifi.update && wifi.update.flashedAt != null ? ((ms) => (ms == null ? '—' : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })))(wifi.update.flashedAt) : '—'}
            />
            <InfoRow label="Update" value={wifi.update ? updateMarkLabel(wifi.update).label : '—'} />
            {wifi.password && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Wi-Fi Password</Typography>
                <WifiPasswordValue value={wifi.password} weight={700} />
              </Box>
            )}
          </Box>
          {/* Super Admin: ids, owner, IP, firmware (.ino.bin + its date) and the
              Wi-Fi networks stored in the chip — two more columns. */}
          {isSuperAdmin && (
            <DeviceTechDetails uno={uno} userId={userId} who={{ isSuperAdmin, userId: realUserId }} open={infoOpen} inGrid />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
